import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ClockState, TournamentStatus } from '@/types';

interface ClockSliceState extends ClockState {
  lastTick: number;
  handTimerRunning: boolean;
  handTimerSeconds: number;
}

const initialState: ClockSliceState = {
  isRunning: false,
  secondsRemaining: 0,
  currentRound: 1,
  isBreak: false,
  pauseCycle: 0,
  lastTick: Date.now(),
  handTimerRunning: false,
  handTimerSeconds: 0,
};

const clockSlice = createSlice({
  name: 'clock',
  initialState,
  reducers: {
    startClock: (state) => {
      state.isRunning = true;
      state.pauseCycle = 0;
      state.lastTick = Date.now();
    },
    
    stopClock: (state) => {
      state.isRunning = false;
      state.pauseCycle = 0;
    },
    
    toggleClock: (state) => {
      state.isRunning = !state.isRunning;
      state.pauseCycle = 0;
      if (state.isRunning) {
        state.lastTick = Date.now();
      }
    },
    
    tick: (state) => {
      if (state.isRunning && state.secondsRemaining > 0) {
        state.secondsRemaining -= 1;
        state.lastTick = Date.now();
      }
      
      if (!state.isRunning) {
        state.pauseCycle = (state.pauseCycle + 1) % 4;
      }
      
      // Handle hand timer
      if (state.handTimerRunning && state.handTimerSeconds > 0) {
        state.handTimerSeconds -= 1;
        if (state.handTimerSeconds <= 0) {
          state.handTimerRunning = false;
        }
      }
    },
    
    setSecondsRemaining: (state, action: PayloadAction<number>) => {
      state.secondsRemaining = Math.max(0, action.payload);
    },
    
    resetClock: (state, action: PayloadAction<number>) => {
      state.secondsRemaining = action.payload;
      state.pauseCycle = 0;
    },
    
    setCurrentRound: (state, action: PayloadAction<number>) => {
      state.currentRound = action.payload;
    },
    
    setIsBreak: (state, action: PayloadAction<boolean>) => {
      state.isBreak = action.payload;
    },
    
    nextRound: (state) => {
      state.currentRound += 1;
      state.isBreak = false;
    },
    
    startBreak: (state) => {
      state.isBreak = true;
    },
    
    endBreak: (state) => {
      state.isBreak = false;
    },
    
    startHandTimer: (state, action: PayloadAction<number>) => {
      state.handTimerRunning = true;
      state.handTimerSeconds = action.payload;
    },
    
    stopHandTimer: (state) => {
      state.handTimerRunning = false;
      state.handTimerSeconds = 0;
    },
    
    setHandTimerSeconds: (state, action: PayloadAction<number>) => {
      state.handTimerSeconds = action.payload;
    },
    
    resetState: (state) => {
      return { ...initialState };
    },
  },
});

export const {
  startClock,
  stopClock,
  toggleClock,
  tick,
  setSecondsRemaining,
  resetClock,
  setCurrentRound,
  setIsBreak,
  nextRound,
  startBreak,
  endBreak,
  startHandTimer,
  stopHandTimer,
  setHandTimerSeconds,
  resetState,
} = clockSlice.actions;

export default clockSlice.reducer;